import { useState } from 'react';

import CustomDatePicker from 'components/form/CustomDatePicker.component';

/**
 * Example usage of CustomDatePicker component
 *
 * This example demonstrates:
 * 1. Leap year support (automatic)
 * 2. Cross-month week selection (Feb 23 - Mar 1)
 * 3. Single date selection
 * 4. Week selection mode
 */

// Example 1: Single Date Selection
export const SingleDateExample = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());

  return (
    <div>
      <h2>Single Date Selection</h2>
      <CustomDatePicker selectedDate={selectedDate} onChange={(date) => setSelectedDate(date)} onClose={() => console.log('Picker closed')} />
      <p>Selected: {selectedDate?.toLocaleDateString()}</p>
    </div>
  );
};

// Example 2: Date Range Selection (including cross-month ranges like Feb 23 - Mar 1)
export const DateRangeSelectionExample = () => {
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);

  const handleRangeChange = (start, end) => {
    setRangeStart(start);
    setRangeEnd(end);
    if (start && end) {
      console.log('Range selected:', {
        start: start.toLocaleDateString(),
        end: end.toLocaleDateString(),
      });
    } else if (start) {
      console.log('Range start selected:', start.toLocaleDateString());
    }
  };

  return (
    <div>
      <h2>Date Range Selection</h2>
      <p>Click a start date, then click an end date to select a range</p>
      <CustomDatePicker selectRange={true} rangeStart={rangeStart} rangeEnd={rangeEnd} onRangeChange={handleRangeChange} />
      {rangeStart && !rangeEnd && <p style={{ color: 'blue' }}>Start: {rangeStart.toLocaleDateString()} (click another date to complete range)</p>}
      {rangeStart && rangeEnd && (
        <p>
          Selected Range: {rangeStart.toLocaleDateString()} - {rangeEnd.toLocaleDateString()}
        </p>
      )}
    </div>
  );
};

// Example 3: Date Selection with Min/Max Bounds
export const BoundedDateExample = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Set bounds: can only select dates in current month
  const minDate = new Date();
  minDate.setDate(1); // First day of current month

  const maxDate = new Date();
  maxDate.setMonth(maxDate.getMonth() + 1);
  maxDate.setDate(0); // Last day of current month

  return (
    <div>
      <h2>Bounded Date Selection</h2>
      <p>Can only select dates in current month</p>
      <CustomDatePicker selectedDate={selectedDate} onChange={(date) => setSelectedDate(date)} minDate={minDate} maxDate={maxDate} onClose={() => console.log('Picker closed')} />
      <p>Selected: {selectedDate?.toLocaleDateString()}</p>
    </div>
  );
};

// Example 4: Cross-Month Range Selection (February 2024 - leap year example)
export const CrossMonthRangeExample = () => {
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);

  const handleRangeChange = (start, end) => {
    setRangeStart(start);
    setRangeEnd(end);

    if (start && end) {
      // Check if it's a cross-month range
      const isCrossMonth = start.getMonth() !== end.getMonth();
      console.log('Range selected:', {
        start: start.toLocaleDateString(),
        end: end.toLocaleDateString(),
        crossMonth: isCrossMonth,
        startMonth: start.toLocaleString('default', { month: 'long' }),
        endMonth: end.toLocaleString('default', { month: 'long' }),
      });
    }
  };

  return (
    <div>
      <h2>Cross-Month Range Selection (Leap Year)</h2>
      <p>Try selecting a range that spans Feb 23-29, 2024 (leap year) into March</p>
      <CustomDatePicker selectRange={true} rangeStart={rangeStart} rangeEnd={rangeEnd} onRangeChange={handleRangeChange} />
      {rangeStart && !rangeEnd && <p style={{ color: 'blue' }}>Start: {rangeStart.toLocaleDateString()} (click another date to complete range)</p>}
      {rangeStart && rangeEnd && (
        <div>
          <p>
            Selected Range: {rangeStart.toLocaleDateString()} - {rangeEnd.toLocaleDateString()}
          </p>
          {rangeStart.getMonth() !== rangeEnd.getMonth() && <p style={{ color: 'green', fontWeight: 'bold' }}>✓ This is a cross-month range!</p>}
        </div>
      )}
    </div>
  );
}; // Example 5: Custom Week Start Day (Sunday vs Monday)
export const CustomWeekStartExample = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [weekStartDay, setWeekStartDay] = useState(1); // 0 = Sunday, 1 = Monday

  return (
    <div>
      <h2>Custom Week Start Day</h2>
      <div style={{ marginBottom: '1rem' }}>
        <label style={{ backgroundColor: '#e8e8e8', width: 'fit-content', padding: '0.5em' }}>
          <input type="radio" checked={weekStartDay === 0} onChange={() => setWeekStartDay(0)} />
          Sunday
        </label>
        <label style={{ backgroundColor: '#e8e8e8', width: 'fit-content', padding: '0.5em' }}>
          <input type="radio" checked={weekStartDay === 1} onChange={() => setWeekStartDay(1)} />
          Monday
        </label>
      </div>
      <CustomDatePicker selectedDate={selectedDate} onChange={(date) => setSelectedDate(date)} weekStartDay={weekStartDay} onClose={() => console.log('Picker closed')} />
      <p>Selected: {selectedDate?.toLocaleDateString()}</p>
    </div>
  );
};

const examples = {
  SingleDateExample,
  DateRangeSelectionExample,
  BoundedDateExample,
  CrossMonthRangeExample,
  CustomWeekStartExample,
};

export default examples;
